import { finalizeUriForProvider } from "@app/lib/api/oauth/utils";
import type { MCPOAuthUseCase } from "@app/types/oauth/lib";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@dust-tt/sparkle";
import { Trans } from "@lingui/react/macro";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";

interface SnowflakeSetupInstructionsProps {
  useCase: MCPOAuthUseCase | null;
}

export function SnowflakeSetupInstructions({
  useCase,
}: SnowflakeSetupInstructionsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const redirectUri = finalizeUriForProvider({
    provider: "snowflake",
    connection: null,
  });

  return (
    <div className="w-full pt-4">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger hideChevron>
          <div className="flex w-full items-center gap-2 rounded-lg border border-border bg-muted/50 p-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted">
            {isOpen ? (
              <ChevronDownIcon className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 shrink-0" />
            )}
            <span>
              <Trans>Snowflake custom OAuth setup guide</Trans>
            </span>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-3 space-y-4 rounded-lg border border-border bg-background p-4 text-sm">
            <p className="text-muted-foreground">
              <Trans>
                Before connecting, you need to create a Custom OAuth Security
                Integration in your Snowflake account. Run the following SQL
                commands as an <strong>ACCOUNTADMIN</strong>:
              </Trans>
            </p>

            <div className="space-y-3">
              <div>
                <p className="mb-2 font-medium text-foreground">
                  <Trans>1. Create the OAuth Security Integration:</Trans>
                </p>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                  {`CREATE SECURITY INTEGRATION dust_oauth
  TYPE = OAUTH
  ENABLED = TRUE
  OAUTH_CLIENT = CUSTOM
  OAUTH_CLIENT_TYPE = 'CONFIDENTIAL'
  OAUTH_REDIRECT_URI = '${redirectUri}'
  OAUTH_ISSUE_REFRESH_TOKENS = TRUE
  OAUTH_REFRESH_TOKEN_VALIDITY = 7776000;`}
                </pre>
              </div>

              <div>
                <p className="mb-2 font-medium text-foreground">
                  <Trans>2. Get the Client ID and Client Secret:</Trans>
                </p>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                  {`SELECT SYSTEM$SHOW_OAUTH_CLIENT_SECRETS('DUST_OAUTH');`}
                </pre>
                <p className="mt-2 text-muted-foreground">
                  <Trans>
                    This returns a JSON object with{" "}
                    <code className="rounded bg-muted px-1">
                      OAUTH_CLIENT_ID
                    </code>{" "}
                    and{" "}
                    <code className="rounded bg-muted px-1">
                      OAUTH_CLIENT_SECRET
                    </code>
                    . Copy these values into the form below.
                  </Trans>
                </p>
              </div>

              <div>
                <p className="mb-2 font-medium text-foreground">
                  <Trans>
                    3. (Optional) Grant the integration to specific roles:
                  </Trans>
                </p>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
                  {`GRANT USAGE ON INTEGRATION dust_oauth TO ROLE <role_name>;`}
                </pre>
              </div>
            </div>

            <p className="text-muted-foreground">
              {useCase === "platform_actions" ? (
                <Trans>
                  <strong>Note:</strong> The warehouse you specify below will be
                  used for all users. The role will also be shared across all
                  users.
                </Trans>
              ) : (
                <Trans>
                  <strong>Note:</strong> The warehouse you specify below will be
                  used for all users. The default role can be overridden by
                  individual users during their personal authentication.
                </Trans>
              )}
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
